import React, { createContext, useContext, useEffect, useState } from "react";

const TrackingParamsContext = createContext({});

const STORAGE_KEY = "sds_tracking_params";

// Common tracking param prefixes/names to capture
const TRACKING_PARAM_PATTERNS = [
  "utm_",      // UTM parameters (utm_source, utm_medium, utm_campaign, etc.)
  "ref",       // Referral codes
  "gclid",     // Google Click ID
  "fbclid",    // Facebook Click ID
  "msclkid",   // Microsoft Click ID
  "ttclid",    // TikTok Click ID
  "li_fat_id", // LinkedIn Click ID
  "aff_",      // Affiliate parameters
  "src",       // Source
  "campaign",  // Campaign
  "adgroup",   // Ad group
  "ad_",       // Ad parameters
  "keyword",   // Keyword
  "placement", // Placement
  "device",    // Device
  "matchtype", // Match type
  "network",   // Network
  "creative",  // Creative ID
  "partner",   // Partner ID
];

/**
 * Check if a param key matches our tracking patterns
 */
function isTrackingParam(key) {
  const lowerKey = key.toLowerCase();
  return TRACKING_PARAM_PATTERNS.some(
    (pattern) => lowerKey.startsWith(pattern) || lowerKey === pattern
  );
}

/**
 * Extract tracking params from URL search string
 */
function extractTrackingParams(searchString) {
  if (!searchString) return {};

  const params = new URLSearchParams(searchString);
  const trackingParams = {};

  for (const [key, value] of params.entries()) {
    if (isTrackingParam(key) && value) {
      trackingParams[key] = value;
    }
  }

  return trackingParams;
}

/**
 * Get stored tracking params from localStorage
 */
function getStoredParams() {
  if (typeof window === "undefined") return {};

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Check if data is not expired (30 days)
      if (parsed.expiry && parsed.expiry > Date.now()) {
        return parsed.params || {};
      }
      // Clear expired data
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) {
    console.error("Error reading tracking params from storage:", e);
  }
  return {};
}

/**
 * Save tracking params to localStorage
 */
function saveParams(params) {
  if (typeof window === "undefined") return;

  try {
    const data = {
      params,
      expiry: Date.now() + 30 * 24 * 60 * 60 * 1000, // 30 days
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error("Error saving tracking params to storage:", e);
  }
}

/**
 * Provider component that captures and stores tracking parameters
 */
export function TrackingParamsProvider({ children }) {
  const [trackingParams, setTrackingParams] = useState({});
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Get existing stored params
    const storedParams = getStoredParams();

    // Extract params from current URL
    const urlParams = extractTrackingParams(window.location.search);

    // Merge: URL params override stored params (latest visit wins)
    const mergedParams = {
      ...storedParams,
      ...urlParams,
    };

    // Add landing page info if this is a new session with params
    if (Object.keys(urlParams).length > 0) {
      mergedParams._landingPage = window.location.pathname;
      mergedParams._landingTime = new Date().toISOString();
    }

    // Only update if we have params
    if (Object.keys(mergedParams).length > 0) {
      saveParams(mergedParams);
      setTrackingParams(mergedParams);
    }

    setIsReady(true);
  }, []);

  // Listen for URL changes (for SPAs)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleUrlChange = () => {
      const urlParams = extractTrackingParams(window.location.search);
      if (Object.keys(urlParams).length > 0) {
        setTrackingParams((prev) => {
          const merged = { ...prev, ...urlParams };
          saveParams(merged);
          return merged;
        });
      }
    };

    // Listen for popstate (back/forward navigation)
    window.addEventListener("popstate", handleUrlChange);

    return () => {
      window.removeEventListener("popstate", handleUrlChange);
    };
  }, []);

  const value = {
    trackingParams,
    isReady,
    // Helper to get params as a flat object for form submission
    getTrackingData: () => ({ ...trackingParams }),
    // Helper to get params as JSON string
    getTrackingJson: () => JSON.stringify(trackingParams),
    // Clear tracking data
    clearTracking: () => {
      if (typeof window !== "undefined") {
        localStorage.removeItem(STORAGE_KEY);
      }
      setTrackingParams({});
    },
  };

  return (
    <TrackingParamsContext.Provider value={value}>
      {children}
    </TrackingParamsContext.Provider>
  );
}

/**
 * Hook to access tracking params
 */
export function useTrackingParams() {
  const context = useContext(TrackingParamsContext);
  if (!context) {
    // Return safe defaults if used outside provider
    return {
      trackingParams: {},
      isReady: true,
      getTrackingData: () => ({}),
      getTrackingJson: () => "{}",
      clearTracking: () => {},
    };
  }
  return context;
}

export default TrackingParamsContext;
